import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, GlobeLock } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from 'src/shared/components/ui/hover-card';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { GNPViews } from 'src/routes/routesConfig';

type TurnedOnPerProjectCardProps = {
    label: string;
    customerId: string;
    /** Whether the product's own setting is Hidden. */
    isSetToHidden: boolean;
};

/** What the setting means for a product that each project also turns on. */
const messageFor = (label: string, isSetToHidden: boolean): string => {
    if (isSetToHidden) {
        return `${label} is hidden from this organisation's users, even on projects that have it turned on in their Project Definition. Its upsell pages are hidden too.`;
    } else {
        return `Gives this organisation access to ${label}. Each project must also have ${label} turned on in its Project Definition.`;
    }
};

/**
 * Says how the setting, which is for the whole organisation, works with the
 * Project Definition of each project, which also turns the product on.
 *
 * A card, not a tooltip, because it holds a link to the Nature Performance
 * admin. The link is an icon, like the links on the other rows, with its name
 * in `aria-label`.
 */
const TurnedOnPerProjectCard = ({ label, customerId, isSetToHidden }: TurnedOnPerProjectCardProps) => (
    <HoverCard openDelay={200} closeDelay={200}>
        <HoverCardTrigger asChild>
            <button type='button' aria-label='Turned on per project' className='inline-flex shrink-0 text-grey-70 hover:text-black'>
                <GlobeLock size={14} aria-hidden />
            </button>
        </HoverCardTrigger>
        <HoverCardPortal>
            <HoverCardContent side='left' className='flex items-start gap-2 text-sm font-normal' data-testid='product-project-note'>
                <span>{messageFor(label, isSetToHidden)}</span>
                <Link
                    to={navRoutesConfig[GNPViews.ORG_MANAGEMENT]({ customerId })}
                    aria-label='Open Nature Performance admin'
                    title='Open Nature Performance admin'
                    className='inline-flex shrink-0 text-grey-70 hover:text-black'
                >
                    <ArrowUpRight size={16} aria-hidden />
                </Link>
            </HoverCardContent>
        </HoverCardPortal>
    </HoverCard>
);

export default TurnedOnPerProjectCard;
